/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import { Grid } from '@atlaskit/primitives/compiled';

import { type ObjectData, type ObjectTileData } from '../types';
import ObjectCell from './object-cell';

type GridSectionProps<T extends ObjectData | ObjectTileData> = {
	testId?: string;
	objects?: T[];
	onObjectClick?: (object: T) => void;
	selectedObject?: string;
	isObjectTile?: boolean;
};

const styles = cssMap({
	root: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
	},
});

/**
 * __Grid section__
 *
 * Component for a grid 'section'.
 * The Object Explorer is made up of a grid with a heading.
 * This grid section component consists of the grid as well as the heading for the section.
 *
 */
const GridSection: <T extends ObjectData | ObjectTileData>({
	testId,
	selectedObject,
	objects,
	onObjectClick,
	isObjectTile,
}: GridSectionProps<T>) => JSX.Element | null = <T extends ObjectData | ObjectTileData>({
	testId,
	selectedObject,
	objects,
	onObjectClick,
	isObjectTile,
}: GridSectionProps<T>) => {
	return objects ? (
		<Grid gap="space.100" xcss={styles.root}>
			{objects.map((object) => (
				<ObjectCell
					testId={testId && `${testId}-object-cell--${object.componentName}`}
					object={object}
					key={object.componentName}
					onObjectClick={onObjectClick as (object: ObjectData | ObjectTileData) => void}
					isSelected={selectedObject === object.packageName}
					isObjectTile={isObjectTile}
				/>
			))}
		</Grid>
	) : null;
};

export default GridSection;
